import {
  ActionIcon,
  Alert,
  Button,
  AppShell,
  Badge,
  Burger,
  Group,
  Menu,
  NavLink,
  ScrollArea,
  Tooltip,
  Stack,
  Text,
  Title,
  UnstyledButton,
} from '@mantine/core'
import { useDisclosure, useLocalStorage } from '@mantine/hooks'
import {
  IconApps,
  IconBuildingBank,
  IconFolders,
  IconHistory,
  IconLayoutDashboard,
  IconSettings,
  IconLogout,
  IconChevronDown,
  IconLayoutSidebarLeftCollapse,
  IconLayoutSidebarLeftExpand,
  IconUser,
  IconUserCircle,
} from '@tabler/icons-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router'
import { can, returnToOwnAccount, signOut } from '../api/session'
import { useLiveUpdates } from '../live-updates'
import { forgetEverything } from '../query-persistence'
import { VaultProvider } from '../finance/vault'
import { CompleteProfilePrompt } from './CompleteProfilePrompt'
import { NotificationBell } from './NotificationBell'
import { UserAvatar } from './UserAvatar'
import { sessionQueryOptions } from '../session-store'

/**
 * Navigation, declared once.
 *
 * Each entry names the permission it needs, so a screen the viewer cannot use
 * is never offered. This only decides what to show - every endpoint behind
 * these screens checks permissions again server-side.
 *
 * **Every entry names one, including the dashboard.** It used to be `null`,
 * meaning "everybody", which was true and unchangeable: there was no way to
 * take the dashboard away from a role without editing this file. Access here
 * is answerable from the role editor, so "everybody" is expressed as a
 * permission everybody is granted rather than as an absence of one.
 */
const NAVIGATION = [
  { to: '/', label: 'Dashboard', icon: IconLayoutDashboard, permission: 'dashboard.read' },
  { to: '/project', label: 'Projects', icon: IconFolders, permission: 'project.read' },
  // Money in and out: invoices, GST and subscriptions now, other parts later,
  // all as tabs inside the one section rather than lines in the sidebar.
  { to: '/accounts', label: 'Accounts', icon: IconBuildingBank, permission: ['finance.read', 'finance.gst.read', 'subscription.read'], matches: ['/accounts', '/invoice', '/subscription'] },
  { to: '/activity', label: 'Activity', icon: IconHistory, permission: 'activity_log.read' },
  // One entry for every internal tool; the page lists them, so the sidebar
  // does not grow a line per tool.
  { to: '/tool', label: 'Tools', icon: IconApps, permission: 'tool.read' },
  // Settings has its own key. It used to borrow user.read, which was the
  // wrong proxy: the moment the area holds notification defaults or billing,
  // "can read the team" stops being a sensible answer to "may this person
  // administer the system".
  { to: '/settings', label: 'Settings', icon: IconSettings, permission: 'settings.access' },
] as const

/** Wide enough for the icon plus NavLink's own padding, and nothing more. */
const RAIL_WIDTH = 64

/**
 * Narrow on purpose. The navigation is four words; the space it was taking
 * belonged to the screen beside it, which is where the work happens.
 */
const FULL_WIDTH = 168

/**
 * The frame every authenticated screen sits in.
 */
export function AppLayout() {
  const [opened, { toggle, close }] = useDisclosure()

  // Remembered per browser, because it is a per-person working preference
  // rather than something the server has any opinion about. useLocalStorage
  // already tolerates storage being unavailable, which it is in a private
  // window.
  const [collapsed, setCollapsed] = useLocalStorage({
    key: 'medulla.nav.collapsed',
    defaultValue: false,
    getInitialValueInEffect: false,
  })
  const { data: user } = useQuery(sessionQueryOptions)
  const queryClient = useQueryClient()
  const navigate = useNavigate()


  /*

    One event stream for the whole application, open for as long as somebody

    is signed in. Everything else follows from it: screens stay in step with

    what colleagues are doing, which is also what makes the cache

    trustworthy enough to navigate without refetching.

  */

  useLiveUpdates(Boolean(user))


  const pathname = useRouterState({ select: (state) => state.location.pathname })

  const logout = useMutation({
    mutationFn: signOut,
    onSettled: async () => {
      // Cleared whether or not the request succeeded: if the session is gone
      // server-side, keeping a stale user in the cache would show a signed-in
      // shell that 401s on every call.
      // Wipes the persisted copy too, or the next person at this machine
      // would see a frame of the last person's work before the redirect.
      forgetEverything(queryClient)
      // The search param is part of the route's contract, so it is
      // passed explicitly even when there is nowhere to return to.
      await navigate({ to: '/login', search: { redirect: undefined, google_error: undefined } })
    },
  })

  return (
    <AppShell
      header={{ height: 56 }}
      // The rail is a desktop affordance only. On a phone the navbar is a
      // drawer that is either open or gone, and a 64px strip of icons
      // permanently taking a fifth of the screen would help nobody.
      navbar={{
        width: collapsed ? RAIL_WIDTH : FULL_WIDTH,
        breakpoint: 'sm',
        collapsed: { mobile: !opened },
      }}
      padding="md"
    >
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between" wrap="nowrap">
          <Group gap="sm" wrap="nowrap">
            <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />

            <Tooltip label={collapsed ? 'Expand menu' : 'Collapse menu'} openDelay={500}>
              <ActionIcon
                visibleFrom="sm"
                variant="subtle"
                color="gray"
                aria-label={collapsed ? 'Expand menu' : 'Collapse menu'}
                aria-expanded={!collapsed}
                onClick={() => setCollapsed(!collapsed)}
              >
                {collapsed ? (
                  <IconLayoutSidebarLeftExpand size={20} />
                ) : (
                  <IconLayoutSidebarLeftCollapse size={20} />
                )}
              </ActionIcon>
            </Tooltip>

            <Title order={3} style={{ letterSpacing: '-0.01em' }}>
              Medulla
            </Title>
          </Group>

          <Group gap="sm" wrap="nowrap">
            {/* The bell sits beside the account, which is where every
                application people already use puts it. */}
            <NotificationBell />

            <Menu position="bottom-end" width={220} withArrow>
            <Menu.Target>
              <UnstyledButton aria-label="Account menu">
                <Group gap="xs" wrap="nowrap">
                  <UserAvatar
                    userId={user?.id ?? ''}
                    initials={user?.initials ?? '?'}
                    hasAvatar={user?.has_avatar ?? false}
                    size={30}
                    version={user?.updated_at}
                  />
                  <Text size="sm" fw={500} visibleFrom="sm">
                    {user?.display_name}
                  </Text>
                  <IconChevronDown size={14} />
                </Group>
              </UnstyledButton>
            </Menu.Target>

            <Menu.Dropdown>
              <Menu.Label>
                <Stack gap={2}>
                  <Text size="xs" fw={500} truncate>
                    {user?.display_name}
                  </Text>
                  <Text size="xs" c="dimmed" truncate>
                    {user?.email}
                  </Text>
                </Stack>
              </Menu.Label>

              {user?.roles.length ? (
                <Menu.Item component="div" style={{ cursor: 'default' }}>
                  <Group gap={4} wrap="wrap">
                    {user.roles.map((role) => (
                      <Badge key={role.id} size="xs" variant="light">
                        {role.name}
                      </Badge>
                    ))}
                  </Group>
                </Menu.Item>
              ) : null}

              <Menu.Divider />

              <Menu.Item
                component={Link}
                to="/profile"
                leftSection={<IconUser size={14} />}
              >
                Profile
              </Menu.Item>

              <Menu.Divider />

              <Menu.Item
                color="red"
                leftSection={<IconLogout size={14} />}
                onClick={() => logout.mutate()}
                disabled={logout.isPending}
              >
                Sign out
              </Menu.Item>
            </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="xs">
        <AppShell.Section grow component={ScrollArea}>
          {NAVIGATION.filter((item) => item.permission === null || (typeof item.permission === 'string' ? [item.permission] : item.permission).some((permission) => can(user, permission))).map(
            (item) => {
              const active = item.to === '/' ? pathname === '/' : ('matches' in item ? item.matches : [item.to]).some((prefix) => pathname.startsWith(prefix))

              const link = (
                <NavLink
                  mb={2}
                  component={Link}
                  to={item.to}
                  // Collapsed, the icon is the only thing identifying the
                  // destination, so it is centred and the label is dropped
                  // rather than clipped to an unreadable stub.
                  label={collapsed ? undefined : item.label}
                  leftSection={<item.icon size={18} />}
                  active={active}
                  onClick={close}
                  aria-label={item.label}
                  styles={
                    collapsed
                      ? { body: { display: 'none' }, section: { marginInlineEnd: 0 }, root: { justifyContent: 'center' } }
                      : undefined
                  }
                />
              )

              // The tooltip is what keeps a rail usable: without it the icons
              // are a guessing game for anybody who does not already know them.
              return collapsed ? (
                <Tooltip key={item.to} label={item.label} position="right" openDelay={300} withArrow>
                  <div>{link}</div>
                </Tooltip>
              ) : (
                <div key={item.to}>{link}</div>
              )
            },
          )}
        </AppShell.Section>

        {/* No "signed in as" along the bottom. Who you are is the avatar and
            the name in the top right, on every page, and saying it twice used
            a permanent strip of the sidebar to repeat something nobody was
            asking. */}
      </AppShell.Navbar>

      <AppShell.Main>
        {/* While a Super Admin is signed in as somebody else, said on every
            screen with the way back - anything done here is recorded as
            that person, so it must never be forgotten which account this is. */}
        {user?.impersonated_by && (
          <Alert color="orange" variant="light" mb="md" icon={<IconUserCircle size={18} />}>
            <Group justify="space-between" gap="sm" wrap="wrap">
              <Text size="sm">
                You are signed in as <b>{user.display_name}</b>. Everything you do is recorded as them.
              </Text>
              <Button
                size="xs"
                variant="filled"
                color="orange"
                onClick={async () => {
                  await returnToOwnAccount()
                  forgetEverything(queryClient)
                  window.location.assign('/settings/users')
                }}
              >
                Return to {user.impersonated_by.display_name}
              </Button>
            </Group>
          </Alert>
        )}
        {/* Around every screen, so unlocking finance survives moving
            between Accounts tabs and locking reaches all of them. */}
        <VaultProvider>
          <Outlet />
        </VaultProvider>
      </AppShell.Main>

      {/* Only ever shown when something is genuinely missing: a password,
          a telephone number Google does not share, or a picture. */}
      {user && <CompleteProfilePrompt user={user} />}
    </AppShell>
  )
}
